import { uniqBy, pick } from 'lodash'
import React from 'react'
import { useMutation, useQuery } from 'react-query'
import hooks from '../../../hooks'

const useCourseAttendees = ({
    courseId,
    onSuccess = () => {}
}) => {
    const [courseAttendees, setCourseAttendees] = React.useState([])
    const cacheKeys = hooks.useCacheKeys()
    const { api, endpoints, url, time } = hooks.useApi()
    const { toastSuccess } = hooks.useToasts({ successMessage: 'Attendance Saved!', onSuccess })
    const cacheKey = [cacheKeys.COURSE_ATTENDEES, courseId]
    const { clearCache } = hooks.useCache({ key: cacheKey, onReload: toastSuccess })
    const { toastError } = hooks.useToasts()
    const { data, isLoading } = useQuery(
        cacheKey,
        () => api.get(url(endpoints.courseAttendees, { courseId })),
        {
            enabled: Boolean(courseId),
            staleTime: time.MINUTE * 10
        }
    )

    const [ saveAttendance, { isLoading: isSaving }] = useMutation(
        () => api.post(url(endpoints.courseAttendees, { courseId }), courseAttendees.map( c => pick(c, ['id'])) ),
        {
            onSuccess: clearCache,
            onError: toastError
        }
    )

    React.useEffect(() => {
        if(!isLoading && data) {
            setCourseAttendees(data)
        }
    }, [data, isLoading ])

    const addAttendee = React.useCallback((attendee) => {
        if (!attendee) {
            return
        }
        const attendees = uniqBy([...courseAttendees, attendee], 'id')
        setCourseAttendees(attendees)
    }, [courseAttendees])

    const removeAttendee = React.useCallback((attendee) => {
        if (!attendee) {
            return
        }
        setCourseAttendees(courseAttendees.filter(c => c.id !== attendee.id))
    }, [courseAttendees])

    return {
        isLoadingCourseAttendees: isLoading,
        isSavingCourseAttendees: isSaving,
        courseAttendees,
        addAttendee,
        removeAttendee,
        saveAttendance
    }
}

export default useCourseAttendees
